import { defineStore } from "pinia";
import { useServiceStore } from "@/store/service";
import { computed, ref, watch } from "vue";
import { DateTime } from "luxon";
import type { IMod } from "@/components/views/mods";
import { usePropertyBuilder } from "@/components/views/config/helpers";
import * as graphql from "@/graphql";
import apolloClient from "@/graphql/client";
import { useApolloErrorHandling } from "@/util/useApolloErrorHandling";

type ModRevision = { id: number; executableId?: number | null; extraData?: string | null };
type MinecraftMod = graphql.MinecraftModFieldsFragment;

export interface MinecraftModPack {
  mod: MinecraftMod;
  // newest version first, one revision per version
  versions: Array<ModRevision>;
  byId: Map<number, ModRevision>;
}

interface Memo<T> {
  loadedAt: number;
  value: T;
}

// The backend rebuilds the catalogue every 5 minutes, so a ctx reload refreshes anything older
const MAX_AGE_MS = 5 * 60 * 1000;

export function modVersionLabel(revision: ModRevision) {
  let version = `v${revision.id}`;
  if (revision.extraData != null) {
    try {
      version = JSON.parse(revision.extraData)["version"] ?? version;
    } catch {
      console.error("extraData is not valid JSON; fall back to the default version string");
    }
  }
  return version;
}

function toPack(mod: graphql.MinecraftModRevisionsQuery["minecraftMods"][number]): MinecraftModPack {
  const { revisions, revisionsSorted, ...fields } = mod!;
  const versions = revisionsSorted
    .filter((entry) => !!entry)
    .sort((a, b) => (b.index ?? 0) - (a.index ?? 0))
    .map((entry) => entry.revision);
  const byId = new Map<number, ModRevision>();
  for (const revision of [...revisions, ...versions]) {
    if (revision) byId.set(revision.id, revision);
  }
  return { mod: fields, versions, byId };
}

export const useMinecraftModsStore = defineStore("minecraftMod", () => {
  const service = useServiceStore();
  const { buildProperty } = usePropertyBuilder();
  const { handleApolloErrorDefault } = useApolloErrorHandling();
  const virtualMod = buildProperty("virtual", "mod", "");
  const installedModList = computed(() => service.ctx.profile?.installedMods ?? []);

  // Mod ids and revision ids repeat between EU and US, so everything is kept per region
  const currentRsid = computed(() => service.ctx.service?.config?.rsid);
  const region = computed(() => (service.ctx.profile?.name === "Minecraft" ? currentRsid.value?.region : undefined));
  const savedMod = computed(() => service.ctx.service?.config?.repoKey);
  const catalogues = ref(new Map<string, Memo<Array<MinecraftMod>>>());
  const packs = ref(new Map<string, Memo<MinecraftModPack>>());
  const pending = ref(new Set<string>());

  const packKey = (repoKey: string) => `${region.value}:${repoKey}`;

  // A customer's list lacks hidden packs, except the one their server runs, which is loaded by its key
  const mods = computed<Array<MinecraftMod>>(() => {
    const catalogue = (region.value && catalogues.value.get(region.value)?.value) || [];
    const repoKey = savedMod.value;
    const saved = repoKey && packs.value.get(packKey(repoKey))?.value.mod;
    if (!saved || catalogue.some((mod) => mod.repoKey === repoKey)) return catalogue;
    const index = catalogue.findIndex((mod, i) => i > 0 && mod.repoKey > saved.repoKey);
    return index < 0 ? [...catalogue, saved] : [...catalogue.slice(0, index), saved, ...catalogue.slice(index)];
  });
  const modsByRepoKey = computed(() => new Map(mods.value.map((mod) => [mod.repoKey, mod])));
  const loading = computed(
    () => !!region.value && !catalogues.value.has(region.value) && pending.value.has(region.value),
  );

  function packLoading(repoKey: string) {
    return !!region.value && !!repoKey && !packs.value.has(packKey(repoKey)) && pending.value.has(packKey(repoKey));
  }

  function revisionsFor(repoKey: string): MinecraftModPack | undefined {
    return region.value ? packs.value.get(packKey(repoKey))?.value : undefined;
  }

  type ApolloErrorLike = Parameters<typeof handleApolloErrorDefault>[0];

  // errorPolicy "all" hands GraphQL errors back on the result instead of throwing them
  function reportErrors(errors: ReadonlyArray<unknown> | undefined) {
    if (!errors?.length) return false;
    handleApolloErrorDefault({ graphQLErrors: errors as ApolloErrorLike["graphQLErrors"] });
    return true;
  }

  async function load<T>(memo: Map<string, Memo<T>>, key: string, fetch: () => Promise<T | undefined>) {
    const loaded = memo.get(key);
    if ((loaded && Date.now() - loaded.loadedAt < MAX_AGE_MS) || pending.value.has(key)) return;
    pending.value.add(key);
    try {
      const value = await fetch();
      if (value !== undefined) memo.set(key, { loadedAt: Date.now(), value });
    } catch (e) {
      handleApolloErrorDefault(e as ApolloErrorLike);
    } finally {
      pending.value.delete(key);
    }
  }

  async function ensurePack(repoKey: string | undefined) {
    const rsid = currentRsid.value;
    if (!rsid || !region.value || !repoKey) return;
    await load(packs.value, packKey(repoKey), async () => {
      const result = await apolloClient.query({
        query: graphql.MinecraftModRevisionsDocument,
        variables: { sid: rsid.id, region: rsid.region, repoKey },
      });
      if (reportErrors(result.errors)) return undefined;
      const mod = result.data?.minecraftMods?.find((entry) => !!entry);
      return mod ? toPack(mod) : undefined;
    });
  }

  async function ensureMods() {
    const rsid = currentRsid.value;
    const catalogueRegion = region.value;
    if (!rsid || !catalogueRegion) return;
    await load(catalogues.value, catalogueRegion, async () => {
      const result = await apolloClient.query({
        query: graphql.MinecraftModsDocument,
        variables: { sid: rsid.id, region: rsid.region },
      });
      if (reportErrors(result.errors)) return undefined;
      return result.data?.minecraftMods?.filter((mod): mod is MinecraftMod => !!mod);
    });
    const catalogue = region.value && catalogues.value.get(region.value)?.value;
    if (catalogue && savedMod.value && !catalogue.some((mod) => mod.repoKey === savedMod.value)) {
      await ensurePack(savedMod.value);
    }
  }

  // every ctx load replaces service.ctx: a server switch, a save, a retry after an error
  watch(
    () => service.ctx,
    () => ensureMods(),
    { immediate: true },
  );

  const filterMods = computed<IMod[]>(() => {
    return mods.value.map((mod) => {
      const timestampInSeconds = mod.lastUpdated
        ? Math.floor(DateTime.fromISO(mod.lastUpdated, { zone: "utc" }).toSeconds())
        : 0;

      return {
        id: mod.id,
        name: mod.name,
        repoKey: mod.repoKey,
        mindRam: mod.mindRam,
        dateCreated: timestampInSeconds,
        lastUpdateTime: timestampInSeconds,
        logo: mod.icon,
        tags: (mod.tags ?? []).map((tag) => (tag?.endsWith("-framework") ? tag.replace("-framework", "") : tag)),
      };
    });
  });

  const availableMods = computed<IMod[]>(() => {
    return filterMods.value?.filter((item: any) => !installedModList.value.includes(item.name)) || [];
  });

  const installedMods = computed<IMod[]>(() =>
    filterMods.value.filter((item) => installedModList.value.includes(item.name) && item.repoKey !== virtualMod.value),
  );

  // includes both installed and active mod
  // returns the list with the active mod at the top
  const realMods = computed<IMod[]>(() => {
    const list = filterMods.value.filter(
      (item) => installedModList.value.includes(item.name) || item.repoKey === virtualMod.value,
    );
    const index = list.findIndex((item) => item.repoKey === virtualMod.value);

    if (index > -1) {
      const [virtual] = list.splice(index, 1);
      list.unshift(virtual);
    }

    return list;
  });

  const modsForDashboard = ref<IMod[]>(realMods.value ?? []);
  watch(realMods, (newMods) => {
    modsForDashboard.value = newMods ?? [];
  });

  const activeMod = computed(() => filterMods.value.find((item) => item.repoKey === virtualMod.value));

  // deletes the mod from the list of mods for the dashboard it's just needed for the frontend
  function deleteMod(repoKey: string) {
    modsForDashboard.value = filterMods.value.filter(
      (item) =>
        (installedModList.value.includes(item.name) || item.repoKey === virtualMod.value) && item.repoKey !== repoKey,
    );
  }

  return {
    mods,
    modsByRepoKey,
    loading,
    ensureMods,
    ensurePack,
    packLoading,
    revisionsFor,
    availableMods,
    installedMods,
    filterMods,
    activeMod,
    modsForDashboard,
    deleteMod,
  };
});
